import React, { useCallback, useMemo, useState } from "react";
import classNames from "classnames";

import type { Transform } from '@dnd-kit/utilities';
import { DndContext, closestCenter, DragEndEvent, DragStartEvent, useSensors, useSensor, PointerSensor } from "@dnd-kit/core";
import { restrictToParentElement, restrictToFirstScrollableAncestor } from "@dnd-kit/modifiers";
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';

import { useAppDispatch, useTypedSelector } from "../../../../store/reducers/use-typed-selector";
import { updateCustomPageModule, updateHomepageModule } from "../../../../store/actions/admin/create-event";

import ModalComponent from '../../../general-ui/modal/modal';
import { PageModule, ReorderContentModalProps, SessionTypesEnum, SortableReorderGroupProps } from "../../../../types/working-model";
import Icon, { ICONS, COLORS } from "../../../general-ui/icon";
import { OptionalComponent } from "../../../../utils/optional-component";
import WaitingIndicator from "../../../general-ui/waiting-indicator/waiting-indicator";
import { UpdatePageModule } from '../../../../connection/page-modules';

import SortableItem from './sortable-content-row';

import "react-datepicker/dist/react-datepicker.css";
import './reorder-sessions-modal.scss';
import { getDisplayedTracksInSpecifiedOrder, fillOutSessionDisplayOptions } from "../../../live-event/modules/agenda/agenda-utils";


const ReorderContentModal: React.FC<ReorderContentModalProps> = ({
	isOpen,
	onClose,
	module,
	defaultImage,
	isLandingPage = false,
	isHomePage = false,
	isCustomPage = false,
	customPageId
}) => {

	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);
	const token = useTypedSelector(state => state.AuthReducer.token);

	const dispatch = useAppDispatch();

	const _allSessions = useMemo(() => workingEvent?.sessions || [], [workingEvent]); // Remove all the non-ondemand sessions;
	const _onDemandSessions = useMemo(() => workingEvent?.sessions?.filter(sess => sess.session_type === SessionTypesEnum.onDemand) || [], [workingEvent]); // Remove all the non-ondemand sessions;

	const _ondemandTracksContent = module?.module_display_info?.session_display_options || {};
	const _displayAllSettings = module.module_display_info?.session_display_all || { liveSessions: true, onDemandSessions: true };
	const _displayOptions = fillOutSessionDisplayOptions(_ondemandTracksContent, _displayAllSettings, _allSessions, false);

	/// set up tracks.
	const _ondemandTracksOrder = module?.module_display_info?.content_track_order || [];
	const _trackOrder = getDisplayedTracksInSpecifiedOrder(_onDemandSessions, _ondemandTracksOrder);

	const [displayOptions, setDisplayOptions] = useState(_displayOptions);
	const [trackOrder, setTrackOrder] = useState<string[]>(_trackOrder);

	const [savingSessions, setSavingSessions] = useState(false);
	const [expandedTracks, setExpandedTracks] = useState(Object.fromEntries(_trackOrder.map(track => [track, true])));

	const disableSessionThumbnails = workingEvent?.settings?.disable_session_thumbnails;

	const sensors = useSensors(useSensor(PointerSensor, {
		activationConstraint: {
			distance: 5,
		},
	}));

	const handleCancel = () => {
		setDisplayOptions(_displayOptions);
		setTrackOrder(_trackOrder);
		onClose();
	};

	const dispatchModuleChanges = (updatedPageModule: PageModule) => {
		if (isCustomPage) {
			if (!customPageId) return; // Handled on entry, customPageId can not be null
			dispatch(updateCustomPageModule(customPageId, updatedPageModule));
		}
		else if (isLandingPage || isHomePage) {
			dispatch(updateHomepageModule(updatedPageModule));
		}
	};

	const getUpdatedPageModule = (content: { [key: string]: number[] }, trackOrder: string[]) => {
		const updatedPageModule = JSON.parse(JSON.stringify(module)) as PageModule;

		if (!updatedPageModule.module_display_info
			|| !updatedPageModule.module_display_info.session_display_options
			|| !updatedPageModule.module_display_info.content_track_order) {

			updatedPageModule.module_display_info = {
				...updatedPageModule.module_display_info,
				session_display_options: {},
				content_track_order: []
			};
		}
		updatedPageModule.module_display_info.session_display_options = content;
		updatedPageModule.module_display_info.content_track_order = trackOrder;

		return updatedPageModule;
	};

	const saveSessionDisplayOptions = async () => {
		if (!token) return;

		setSavingSessions(true);
		const updatedPageModule = getUpdatedPageModule(displayOptions, trackOrder);

		await UpdatePageModule(token, updatedPageModule);
		dispatchModuleChanges(updatedPageModule);
		setSavingSessions(false);
		onClose();
	};

	const handleDragGroupEnd = useCallback((event: DragEndEvent) => {
		const { active, over } = event;
		if (!event.over) return;
		if (active?.id === over?.id) return;
		const target = event.activatorEvent.target as Element;
		const closestGroup = target.closest(`.track-group`);
		closestGroup?.classList.toggle('active-grab');
		const track = closestGroup?.getAttribute("data-track-id");

		if (!track) return;
		const _activeID = String(active?.id);
		const _overID = String(over?.id);
		if (!_activeID || !_overID) return;

		setTrackOrder(prev => {
			const activeIndex = prev.indexOf(_activeID);
			const overIndex = prev.indexOf(_overID);
			const newArray = arrayMove(prev, activeIndex, overIndex);
			return [...newArray];
		});
	}, []);

	const handleDragGroupStart = useCallback((event: DragStartEvent) => {
		const targetID = event.active.id;
		const target = document.querySelector(`.track-group[data-track-id="${String(targetID)}"]`);
		if (!target) return;
		target.classList.toggle('active-grab');
	}, []);

	const sortableContextOrderOfTracksItems = trackOrder.map(track => track);

	const SortableReorderGroup: React.FC<SortableReorderGroupProps> = ({
		track,
		displayOptions,
		id,
		disableSessionThumbnails,
		defaultImage,
		setDisplayOptions,
		isExpanded,
		setExpandedTracks }) => {
		const {
			attributes,
			listeners,
			setNodeRef,
			transform,
			transition,
		} = useSortable({ id: id });


		// prevent scaling above and below 1
		const preventScalingTransform = (transform: Transform | null) => {
			if (!transform) return null;
			return {
				...transform,
				scaleY: 1,
			};
		};

		const style = {
			transform: CSS.Transform.toString(preventScalingTransform(transform)) || "",
			transition,
		};


		const handleDragEnd = (event: DragEndEvent) => {
			const { active, over } = event;
			if (!event.over) return;
			if (active?.id === over?.id) return;
			const target = event.activatorEvent.target as Element;
			const closestGroup = target.closest(`.track-group`);
			const track = closestGroup?.getAttribute("data-track-id");

			if (!track) return;

			const _activeID = String(active?.id);
			const _overID = String(over?.id);

			// checks that the ids exist on the selected element
			if (!_activeID || !_overID || !_activeID.includes(`${track}-`) || !_overID.includes(`${track}-`)) return;

			const activeID = Number(_activeID.split(`${track}-`)[1]);
			const overID = Number(_overID.split(`${track}-`)[1]);

			setDisplayOptions(prev => {
				const activeIndex = prev[track].indexOf(activeID);
				const overIndex = prev[track].indexOf(overID);
				const newArray = arrayMove(prev[track], activeIndex, overIndex);

				return {
					...prev,
					[track]: newArray
				};
			});
		};

		const shouldDisplayTrack = () => {
			const onDemandSessionIds: number[] = _onDemandSessions.map(session => session.session);
			return displayOptions[track].some(sessionID => onDemandSessionIds.includes(sessionID));
		};

		const draggableRows = useCallback(() => (

			displayOptions[track].map((sessionID, i: number) => {
				const session = _onDemandSessions?.find(s => s.session === sessionID);
				if (!session) return <React.Fragment key={`${track}-${i}`}></React.Fragment>;
				return (
					<SortableItem
						key={`${track}-${i}`}
						id={`${track}-${String(sessionID)}`}
						session={session}
						disableSessionThumbnails={disableSessionThumbnails}
						defaultImage={defaultImage}
					/>
				);
			})
		), [defaultImage, disableSessionThumbnails, displayOptions, track]);


		const sortableContextItems = displayOptions[track].map(sessionID => {
			const session = _onDemandSessions?.find(s => s.session === sessionID);
			return (`${track}-${String(session?.session)}`);
		});

		const handleExpandedClick = () => {
			setExpandedTracks(prev => ({ ...prev, [track]: !prev[track] }));
		};

		const alteredTrackTitle = track === "bl_untracked" ? "Uncategorized" : track;

		if (!shouldDisplayTrack()) return (<></>);

		return (
			<div className="track-group" key={track} data-track-id={track} ref={setNodeRef} style={style}  >
				<div className="track-group-header">
					<div className="left-column">
						<div className="drag-handle" {...attributes} {...listeners}>
							<Icon name={ICONS.DRAG_HANDLE} size={14} color={COLORS.GRAY} />
						</div>
						<h2 className="trackTitle">{alteredTrackTitle}</h2>
					</div>
					<div className={classNames('new-dropdown-icon', { 'isopen': isExpanded, 'isnotexpanded': !isExpanded })} onClick={handleExpandedClick}></div>
				</div>
				<OptionalComponent display={isExpanded}>
					<DndContext
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
						modifiers={[restrictToParentElement, restrictToFirstScrollableAncestor]}
						autoScroll={true}
					>
						<SortableContext
							items={sortableContextItems}
							strategy={verticalListSortingStrategy}
						>
							{draggableRows()}
						</SortableContext>
					</DndContext>
				</OptionalComponent>
			</div>
		);
	};

	return (
		<>
			<ModalComponent
				addMaxHeightClass={true}
				open={isOpen}
				onRequestClose={handleCancel}
				title="Reorder content"
				subtitle="Only on-demand videos can be reordered"
				cancellable
				closeable={false}
				size={"small"}
				padTitle={false}
				footer={(
					<div className="session-modal-footer">
						<div className="action-buttons">
							<button onClick={saveSessionDisplayOptions} className="primary" disabled={savingSessions}>{savingSessions ? <WaitingIndicator /> : "OK"}</button>
						</div>
					</div>
				)}
				portalClassName="session-modal-portal"
			>
				<DndContext
					sensors={sensors}
					collisionDetection={closestCenter}
					onDragStart={handleDragGroupStart}
					onDragEnd={handleDragGroupEnd}
					modifiers={[restrictToParentElement, restrictToFirstScrollableAncestor]}
					autoScroll={false}
				>
					<SortableContext
						items={sortableContextOrderOfTracksItems}
						strategy={verticalListSortingStrategy}
					>
						{trackOrder.map(track =>
							<React.Fragment key={track}>
								<OptionalComponent display={displayOptions[track] && displayOptions[track]?.length > 0}>
									<SortableReorderGroup
										id={track}
										track={track}
										displayOptions={displayOptions}
										disableSessionThumbnails={disableSessionThumbnails}
										defaultImage={defaultImage}
										setDisplayOptions={setDisplayOptions}
										isExpanded={expandedTracks[track]}
										setExpandedTracks={setExpandedTracks}
									/>
								</OptionalComponent>
							</React.Fragment>
						)}
					</SortableContext>
				</DndContext>
			</ModalComponent>
		</>
	);
};

export default ReorderContentModal;